import { useCallback, useEffect, useRef, useState } from 'react';

// Default activation band: a section is "active" when it sits in the top 10-30% slice of the viewport.
const DEFAULT_ACTIVATION_ZONE_TOP_PERCENT = 10;
const DEFAULT_ACTIVATION_ZONE_HEIGHT_PERCENT = 20;

const useScrollSpy = ({
    sections,
    enabled = true,
    initialActiveId,
    activationZoneTopPercent = DEFAULT_ACTIVATION_ZONE_TOP_PERCENT,
    activationZoneHeightPercent = DEFAULT_ACTIVATION_ZONE_HEIGHT_PERCENT,
}) => {
    const [activeSectionId, setActiveSectionId] = useState(initialActiveId ?? sections[0]?.id);
    const sectionRefs = useRef({});
    const sentinelRef = useRef(null);
    const isProgrammaticScrollRef = useRef(false);
    const isAtScrollEndRef = useRef(false);

    const setSectionRef = useCallback(
        id => node => {
            sectionRefs.current[id] = node;
        },
        []
    );

    const setSentinelRef = useCallback(node => {
        sentinelRef.current = node;
    }, []);

    const scrollToSection = useCallback(id => {
        const node = sectionRefs.current[id];
        if (!node?.scrollIntoView) return;
        setActiveSectionId(id);
        isProgrammaticScrollRef.current = true;
        const release = () => {
            isProgrammaticScrollRef.current = false;
        };
        document.addEventListener('scrollend', release, { capture: true, once: true });
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    useEffect(() => {
        if (!enabled || !sections?.length) return undefined;
        const lastSection = sections[sections.length - 1];
        const activationZoneBottomPercent = 100 - activationZoneTopPercent - activationZoneHeightPercent;

        const observer = new IntersectionObserver(
            entries => {
                if (isProgrammaticScrollRef.current) return;
                if (isAtScrollEndRef.current) {
                    setActiveSectionId(lastSection.id);
                    return;
                }
                const visible = entries.filter(entry => entry.isIntersecting);
                if (visible.length === 0) return;
                const topmost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
                setActiveSectionId(topmost.target.dataset.sectionId);
            },
            {
                rootMargin: `-${activationZoneTopPercent}% 0px -${activationZoneBottomPercent}% 0px`,
                threshold: 0,
            }
        );

        Object.values(sectionRefs.current).forEach(node => {
            if (node) observer.observe(node);
        });

        // The last section may be too short for its top to reach the activation band; in that
        // case the main observer never marks it active. A sentinel element placed at the end of
        // the content lets us detect "no more content to scroll past" via IntersectionObserver
        // instead of a per-tick scroll handler. When leaving that state, recompute once against
        // the activation band so the previous section isn't skipped.
        const sentinelObserver = new IntersectionObserver(
            ([entry]) => {
                if (isProgrammaticScrollRef.current) return;
                const reached = entry.isIntersecting;
                const wasReached = isAtScrollEndRef.current;
                isAtScrollEndRef.current = reached;
                if (reached) {
                    setActiveSectionId(lastSection.id);
                } else if (wasReached) {
                    const topLine = (window.innerHeight * activationZoneTopPercent) / 100;
                    const bottomLine =
                        (window.innerHeight * (activationZoneTopPercent + activationZoneHeightPercent)) / 100;
                    let topmost = null;
                    sections.forEach(section => {
                        const node = sectionRefs.current[section.id];
                        if (!node) return;
                        const rect = node.getBoundingClientRect();
                        if (rect.top < bottomLine && rect.bottom > topLine && (!topmost || rect.top < topmost.top)) {
                            topmost = { id: section.id, top: rect.top };
                        }
                    });
                    if (topmost) setActiveSectionId(topmost.id);
                }
            },
            { threshold: 0 }
        );

        if (sentinelRef.current) sentinelObserver.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
            sentinelObserver.disconnect();
        };
    }, [enabled, sections, activationZoneTopPercent, activationZoneHeightPercent]);

    return { activeSectionId, setActiveSectionId, setSectionRef, setSentinelRef, scrollToSection };
};

export default useScrollSpy;
